import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, Sparkles } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import ServicesCarousel from '../components/ServicesCarousel';
import { obtenerProductos } from '../api/productos';
import { obtenerCarrusel } from '../api/carrusel';
import { crearReserva } from '../api/reservas';
import { useNotify } from '../components/NotificationProvider';
import BookingForm from '../components/BookingForm';
import { buildServiceCategories, normalizeCategoryKey } from '../utils/helpers';

const ClientServices = ({ user, onLoginClick }) => {
  const [products, setProducts] = useState([]);
  const [carouselImages, setCarouselImages] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showBookingForm, setShowBookingForm] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const cargarProductos = async () => {
      const [productosData, carruselData] = await Promise.all([
        obtenerProductos(),
        obtenerCarrusel().catch(() => [])
      ]);
      setProducts(productosData);
      setCarouselImages(Array.isArray(carruselData) ? carruselData : []);
    };
    cargarProductos();
  }, []);

  const availableProducts = products.filter((product) => (
    product.Disponible === true ||
    product.Disponible === 1 ||
    String(product.Disponible).toLowerCase() === 'true'
  ));
  const categories = buildServiceCategories(availableProducts);

  // Si venimos desde /productos con un producto seleccionado, abrir el formulario
  useEffect(() => {
    const preselect = location?.state?.selectProduct;
    const requireLogin = !!location?.state?.requireLogin;
    if (preselect) {
      setSelectedProduct(preselect);
      setShowBookingForm(true);
      // Si requiere login y no hay usuario, abrir modal de login y avisar
      if (requireLogin && !user && typeof onLoginClick === 'function') {
        const id = notifyInfo('Inicia sesión o crea tu cuenta para reservar', { duration: 5000 });
        try { window.lastLoginPromptId = id; } catch {}
        onLoginClick();
      }
      // Limpia el estado de navegación para evitar reabrir al volver
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location?.state]);

  const filteredProducts = availableProducts.filter((product) => {
    const matchesSearch =
      (product.Nombre || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      (product.Descripcion || '')
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    const matchesFilter =
      filter === 'all' ||
      normalizeCategoryKey(product.Categoria || product.CategoriaTexto) === filter;
    return matchesSearch && matchesFilter;
  });

  const { success: notifySuccess, error: notifyError, info: notifyInfo } = useNotify();

  const handleSelectProduct = (product) => {
    if (!user) {
      const id = notifyInfo('Inicia sesión o crea tu cuenta para reservar', { duration: 5000 });
      try { window.lastLoginPromptId = id; } catch {}
      if (typeof onLoginClick === 'function') onLoginClick();
      return;
    }
    setSelectedProduct(product);
    setShowBookingForm(true);
  };

  const handleBookingSubmit = async (bookingData) => {
    if (!user || !user.id) {
      notifyError('Debes iniciar sesión para reservar', { duration: 5000 });
      return;
    }

    // Normaliza usando las claves en español
    const fechaISO = bookingData.fecha
      ? new Date(bookingData.fecha).toISOString().slice(0, 10)
      : null;

    const horaHM = bookingData.hora ? String(bookingData.hora).slice(0, 5) : null;
    const manicuristaId = Number(bookingData.manicuristaId);

    if (!fechaISO || !horaHM) {
      notifyError('Selecciona fecha y hora válidas', { duration: 5000 });
      return;
    }

    if (!Number.isInteger(manicuristaId) || manicuristaId <= 0) {
      notifyError('No hay manicurista disponible para este horario', { duration: 5000 });
      return;
    }

    // Normalizar hora a HH:mm:ss para backend
    const horaFull = /:\d{2}$/.test(horaHM) ? horaHM : `${horaHM}:00`;

    const reserva = {
      UsuarioId: user.id,
      ProductoId: bookingData.productId,
      ManicuristaId: manicuristaId,
      Fecha: fechaISO,                 // 'YYYY-MM-DD'
      Hora: horaFull,                  // 'HH:mm:ss'
      Estado: 'pendiente',
      Notas: bookingData.notas || '',
      NombreCliente: user.nombreCompleto || user.username,
      Email: user.email || bookingData.clientEmail,
      Telefono: bookingData.clientPhone || user.telefono
    };

    try {
      const resp = await crearReserva(reserva);
      const nro = resp?.numeroReserva || resp?.NumeroReserva || resp?.id;
      notifySuccess(`Reserva creada (#${nro})`, { duration: 4500 });
      // NO cerrar el formulario inmediatamente - dejar que el usuario vea los slots actualizados
      // y pueda hacer otra reserva si desea
      // setShowBookingForm(false);
      // setSelectedProduct(null);
    } catch (error) {
      notifyError(error.message || 'Error al crear reserva', { duration: 6000 });
    }
  };

  const handleBackToServices = () => {
    setShowBookingForm(false);
    setSelectedProduct(null);
  };

  return (
    <div>
      {showBookingForm && selectedProduct ? (
        <BookingForm
          product={selectedProduct}
          onSubmit={handleBookingSubmit}
          onBack={handleBackToServices}
          user={user}
        />
      ) : (
        <>
          <div className="max-w-6xl mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-extrabold mb-3 text-center bg-clip-text text-transparent bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-600 drop-shadow-sm">
              Nuestros Servicios
            </h1>
            <div className="flex justify-center mb-6">
              <span className="inline-block h-[3px] w-24 md:w-32 rounded-full bg-gradient-to-r from-pink-400 via-purple-500 to-indigo-500" />
            </div>
            <ServicesCarousel images={carouselImages} />
            {!user && (
              <p className="text-gray-700 mb-10 max-w-2xl mx-auto text-center leading-relaxed">
                Explora parte del catálogo disponible.
                {' '}
                <button
                  type="button"
                  className="font-bold text-purple-700 hover:text-purple-800"
                  onClick={() => { try { window.dispatchEvent(new CustomEvent('open-login')); } catch {} if (typeof onLoginClick === 'function') onLoginClick(); }}
                >
                  Inicia sesión
                </button>
                {' '}o{' '}
                <button
                  type="button"
                  className="font-bold text-pink-600 hover:text-pink-700"
                  onClick={() => navigate('/register', { replace: false, state: { from: '/' } })}
                >
                  crea tu cuenta
                </button>
                {' '}para reservar y ver más detalles personalizados.
              </p>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >

            <motion.div
              className="mx-auto mb-8 flex w-full max-w-6xl gap-3 overflow-x-auto px-4 pb-2 sm:flex-wrap sm:justify-center sm:overflow-visible"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {categories.map((category, index) => (
                <motion.button
                  key={category.key}
                  onClick={() => setFilter(category.key)}
                  className={`flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl px-5 py-3 font-medium transition-all duration-300 ${
                    filter === category.key
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                      : 'bg-white/80 text-gray-700 hover:bg-white hover:shadow-md'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                >
                  <Filter className="w-4 h-4" />
                  {category.label}
                </motion.button>
              ))}
            </motion.div>

            <motion.div
              className="mb-8 flex justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <input
                type="text"
                placeholder="Buscar servicio..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full max-w-md px-6 py-3 rounded-2xl border-2 border-gray-200 focus:border-pink-500 focus:outline-none"
              />
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {filteredProducts.map((product) => (
                <ProductCard
                  key={product.Id}
                  product={product}
                  onSelect={handleSelectProduct}
                />
              ))}
            </motion.div>

            {filteredProducts.length === 0 && (
              <motion.div
                className="text-center py-16"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  No hay servicios disponibles
                </h3>
                <p className="text-gray-500">
                  Intenta con otra categoría o vuelve más tarde.
                </p>
              </motion.div>
            )}
          </motion.div>
        </>
      )}
    </div>
  );
};

export default ClientServices;
